import React, { useContext, useState } from 'react';

import { AmpPropsContext } from '../../context/amp-props/amp-props-provider';
import { IAmpPropsTag } from '../../typings/amp';
import { LiveblogTenant } from '../../typings/liveblog-themes';
import { IClassNames, makeClassNames } from '../../utils/styles';
import IconArrowDown from '../icons/icon-arrow-down';
import IconArrowUp from '../icons/icon-arrow-up';
import { AcceptedCookiesConfig } from '../liveblog/liveblog';
import PinnedPost from '../pinned-post/pinned-post';
import Post, { IPost } from '../post/post';
import './timeline.scss';

export interface TimelineProps {
  posts?: IPost[];
  isPinned?: boolean;
  dateNow: Date;
  newPostsCounter: number;
  acceptedCookiesConfig: AcceptedCookiesConfig;
  tenant?: LiveblogTenant;
  isWebView?: boolean;
  ampProps?: Array<IAmpPropsTag>;
}

interface TimelineSectionProps {
  postId: string;
  children: JSX.Element;
  cn: IClassNames;
  index: number;
  newPostsCounter: number;
}

const classNames = (isPinned: boolean, hasReadmore: boolean, isReadmoreOpen: boolean): IClassNames =>
  makeClassNames({
    Timeline: 'timeline',
    TimelineSection: 'timeline__section',
    TimelineWrapper: 'timeline__aside',
    TimelineDot: 'timeline__dot',
    TimelineDotActive: `timeline__dot--active${isPinned ? '--pinned' : ''}`,
    TimelineLine: 'timeline__line',
    TimelineMain: 'timeline__main',
    TimelineReadmoreContainer: [
      hasReadmore && 'timeline__readmore__container',
      hasReadmore && !isReadmoreOpen && 'readmore_closed',
    ],
    TimelineReadMoreAction: 'timeline__readmore__action',
  });

// SUPPORT SUB-COMPONENTS
const TimelineSection = ({ postId, children, cn, index, newPostsCounter }: TimelineSectionProps) => (
  <section className={cn.TimelineSection} key={postId} id={postId}>
    <div className={cn.TimelineWrapper}>
      <div className={`${cn.TimelineDot} ${index < newPostsCounter ? cn.TimelineDotActive : ''}`} />
      <div className={cn.TimelineLine} />
    </div>
    <div className={cn.TimelineMain}>{children}</div>
  </section>
);

const Timeline: React.FC<TimelineProps> = ({
  posts = [] /* istanbul ignore next: default value */,
  isPinned = false,
  dateNow,
  newPostsCounter,
  acceptedCookiesConfig,
  tenant,
  isWebView,
}) => {
  const { isAmp } = useContext(AmpPropsContext);

  const hasReadmore = isPinned && !isAmp;
  const [isReadmoreOpen, setReadmoreOpen] = useState<boolean>(false);

  const cn = classNames(isPinned, hasReadmore, isReadmoreOpen);

  const readMoreClick = () => {
    if (isReadmoreOpen) {
      setReadmoreOpen(false);
    } else {
      setReadmoreOpen(true);
    }
  };

  return (
    <div className={cn.Timeline}>
      <div className={cn.TimelineReadmoreContainer} style={isReadmoreOpen ? { height: 'auto' } : {}}>
        {posts.map((post, index) => (
          <TimelineSection
            key={post.postId}
            postId={post.postId}
            cn={cn}
            index={index}
            newPostsCounter={newPostsCounter}
          >
            {hasReadmore ? (
              <PinnedPost
                {...post}
                dateNow={dateNow}
                acceptedCookiesConfig={acceptedCookiesConfig}
                tenant={tenant}
                isWebView={isWebView}
              />
            ) : (
              <Post
                {...post}
                dateNow={dateNow}
                acceptedCookiesConfig={acceptedCookiesConfig}
                tenant={tenant}
                isWebView={isWebView}
              />
            )}
          </TimelineSection>
        ))}
      </div>

      {hasReadmore && (
        <label className={cn.TimelineReadMoreAction} role="button" onClick={readMoreClick}>
          {isReadmoreOpen ? (
            <>
              Leggi di meno <IconArrowUp width={10} />
            </>
          ) : (
            <>
              Leggi di più <IconArrowDown width={10} />
            </>
          )}
        </label>
      )}
    </div>
  );
};

export default Timeline;
